import React from "react";
import axios from "axios";
import Loading from "../../components/common/loading";
import TextInput from "../../components/common/inputs/textInput";
import ToggleInput from "../../components/common/inputs/toggleInput";
import Pagination from "../../components/common/pagination";
import NotificationManager from "../../utils/notificationManager";
import PDUAccessDropdown from "../../components/users/pduAccessDropdown.js";
import {
  AppPageHeader,
  AppToolbar,
  AppToolbarField,
  AppToolbarGrid,
} from "../../components/common/appShell";
import { Link } from "react-router-dom";

const DEFAULT_PRIMARY_COLOR = "#1d4ed8";
const DEFAULT_PRIMARY_FONT_COLOR = "#ffffff";

const getCssVariable = (variableName, fallback) => {
  if (typeof window === "undefined" || !window.getComputedStyle) {
    return fallback;
  }

  const value = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(variableName);

  return value && value.trim() ? value.trim() : fallback;
};

const parseColorToRgb = (color) => {
  if (!color) {
    return null;
  }

  const trimmed = color.trim();
  const hexMatch = trimmed.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }
    const intVal = parseInt(hex, 16);
    return {
      r: (intVal >> 16) & 255,
      g: (intVal >> 8) & 255,
      b: intVal & 255,
    };
  }

  const rgbMatch = trimmed.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\s*\)$/i
  );
  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
    };
  }

  return null;
};

const applyAlphaToColor = (color, alpha, fallback) => {
  const rgb = parseColorToRgb(color);
  if (!rgb) {
    return fallback;
  }
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

const mixColors = (sourceColor, targetColor, weight, fallback) => {
  const source = parseColorToRgb(sourceColor);
  const target = parseColorToRgb(targetColor);
  if (!source || !target) {
    return fallback;
  }

  const clampedWeight = Math.min(Math.max(weight, 0), 1);
  const r = Math.round(source.r * (1 - clampedWeight) + target.r * clampedWeight);
  const g = Math.round(source.g * (1 - clampedWeight) + target.g * clampedWeight);
  const b = Math.round(source.b * (1 - clampedWeight) + target.b * clampedWeight);
  return `rgb(${r}, ${g}, ${b})`;
};

const getReadableAccentColor = (color, darkMode) => {
  if (!color) {
    return darkMode ? "#bfdbfe" : DEFAULT_PRIMARY_COLOR;
  }

  const rgb = parseColorToRgb(color);
  if (!rgb) {
    return darkMode ? "#bfdbfe" : color;
  }

  if (!darkMode) {
    return color;
  }

  const luminance =
    (0.2126 * (rgb.r / 255) + 0.7152 * (rgb.g / 255) + 0.0722 * (rgb.b / 255));

  if (luminance < 0.35) {
    return mixColors(color, "#ffffff", 0.6, "#bfdbfe");
  }

  if (luminance > 0.85) {
    return mixColors(color, "#1f2937", 0.5, "#bfdbfe");
  }

  return color;
};

const getRoleName = (roles = [], roleId) =>
  roles.find((role) => String(role.id) === String(roleId))?.name || "";

export default class UserTeamMembers extends React.Component {
  render() {
    if (!this.props.resources.userEnterpriseLoaded) {
      return <Loading />;
    }

    return (
      <div className="grid-x grid-padding-x ">
        <ManageTeamMembers {...this.props} />
      </div>
    );
  }
}

class ManageTeamMembers extends React.Component {
  state = {
    searchTerm: "",
    statusFilter: "all",
    adminFilter: "all",
    roleFilter: "all",
    addUserModalOpen: false,
    addUserMode: "invite",
    modalSubmitting: false,
    modalError: "",
    inviteEmail: "",
    inviteHasAdminPrivileges: false,
    invitePasswordLoginDisabled: false,
    inviteSendSetupEmail: true,
    invitePDUs: [],
    usersPager: null,
    usersPageItems: [],
    dark:
      typeof document !== "undefined" &&
      document.body.classList.contains("dark-mode"),
    primaryColor: getCssVariable("--primary-color", DEFAULT_PRIMARY_COLOR),
    primaryFontColor: getCssVariable(
      "--primary-font-color",
      DEFAULT_PRIMARY_FONT_COLOR
    ),
  };

  componentDidMount() {
    if (
      !this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoading
    ) {
      this.props.fetchResource("groups");
    }

    if (
      !this.props.resources.rolesLoaded &&
      !this.props.resources.rolesLoading
    ) {
      this.props.fetchResource("roles");
    }

    if (typeof document !== "undefined") {
      this.darkObserver = new MutationObserver(() => {
        const isDark = document.body.classList.contains("dark-mode");
        this.setState((prev) =>
          prev.dark === isDark ? null : { dark: isDark }
        );
      });

      this.darkObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });

      this.themeObserver = new MutationObserver(() => {
        this.updateThemeColors();
      });

      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["style", "class"],
      });

      this.updateThemeColors();
    }
  }

  componentWillUnmount() {
    this.darkObserver && this.darkObserver.disconnect();
    this.themeObserver && this.themeObserver.disconnect();
  }

  updateThemeColors = () => {
    const primaryColor = getCssVariable("--primary-color", DEFAULT_PRIMARY_COLOR);
    const primaryFontColor = getCssVariable(
      "--primary-font-color",
      DEFAULT_PRIMARY_FONT_COLOR
    );

    this.setState((prev) => {
      if (
        prev.primaryColor === primaryColor &&
        prev.primaryFontColor === primaryFontColor
      ) {
        return null;
      }

      return { primaryColor, primaryFontColor };
    });
  };

  getEnterpriseUsers = () => {
    const users = this.props.resources.userEnterprise?.users || [];
    return users
      .map((user) => ({
        ...user,
        paginationId: `${user.user_id || user.id || user.email_id}`,
      }))
      .sort((a, b) => {
        const aIsAdmin = a.is_enterprise_admin || a.has_admin_privileges;
        const bIsAdmin = b.is_enterprise_admin || b.has_admin_privileges;
        if (aIsAdmin !== bIsAdmin) {
          return aIsAdmin ? -1 : 1;
        }
        return (a.email_id || "").localeCompare(b.email_id || "");
      });
  };

  getFilteredUsers = () => {
    const lowered = this.state.searchTerm.trim().toLowerCase();
    const { statusFilter, adminFilter, roleFilter } = this.state;
    return this.getEnterpriseUsers().filter((user) => {
      const email = (user.email_id || "").toLowerCase();
      const name = (user.full_name || user.display_name || "").toLowerCase();
      const matchesSearch =
        !lowered || email.includes(lowered) || name.includes(lowered);
      const isInactive = !user.active || !user.active_in_enterprise;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && !isInactive) ||
        (statusFilter === "inactive" && isInactive);
      const isAdmin = user.is_enterprise_admin || user.has_admin_privileges;
      const matchesAdmin =
        adminFilter === "all" ||
        (adminFilter === "admin" && isAdmin) ||
        (adminFilter === "member" && !isAdmin);
      const userRoleId =
        user.role_id === undefined || user.role_id === null || user.role_id === ""
          ? ""
          : String(user.role_id);
      const matchesRole =
        roleFilter === "all" ||
        (roleFilter === "none" && !userRoleId) ||
        userRoleId === roleFilter;

      return matchesSearch && matchesStatus && matchesAdmin && matchesRole;
    });
  };

  getRoleOptions = () => {
    const roles = this.props.resources.roles || [];
    const users = this.getEnterpriseUsers();
    const roleMap = new Map();

    roles.forEach((role) => {
      if (role?.id !== undefined && role?.id !== null) {
        roleMap.set(String(role.id), role.name || `Role ${role.id}`);
      }
    });

    users.forEach((user) => {
      if (user.role_id !== undefined && user.role_id !== null && user.role_id !== "") {
        const roleId = String(user.role_id);
        if (!roleMap.has(roleId)) {
          roleMap.set(roleId, user.role?.name || `Role ${roleId}`);
        }
      }
    });

    return Array.from(roleMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  updateMemberFilter = (key, value) => {
    this.setState({
      [key]: value,
      usersPager: null,
      usersPageItems: [],
    });
  };

  clearMemberFilters = () => {
    this.setState({
      searchTerm: "",
      statusFilter: "all",
      adminFilter: "all",
      roleFilter: "all",
      usersPager: null,
      usersPageItems: [],
    });
  };

  getEnterpriseStatusPills = (user) => {
    const pills = [];
    const enterprise = this.props.resources.userEnterprise;
    const isInactive = !user.active || !user.active_in_enterprise;
    const { primaryColor } = this.state;
    const accentColor = primaryColor || DEFAULT_PRIMARY_COLOR;
    const accentBackgroundLight = applyAlphaToColor(
      accentColor,
      0.14,
      "rgba(59, 130, 246, 0.14)"
    );
    const accentBackgroundDark = applyAlphaToColor(
      accentColor,
      0.28,
      "rgba(59, 130, 246, 0.28)"
    );

    if (isInactive) {
      pills.push({
        label: "Inactive",
        color: "#b71c1c",
        darkColor: "#fca5a5",
        backgroundLight: "rgba(183, 28, 28, 0.08)",
        backgroundDark: "rgba(248, 113, 113, 0.18)",
      });
    } else {
      pills.push({
        label: "Active",
        color: "#1b5e20",
        darkColor: "#4ade80",
        backgroundLight: "rgba(27, 94, 32, 0.08)",
        backgroundDark: "rgba(34, 197, 94, 0.18)",
      });
    }

    if (enterprise?.email_verification_required && !user.email_verified) {
      pills.push({
        label: "Email verification required",
        color: "#92400e",
        darkColor: "#fbbf24",
        backgroundLight: "rgba(217, 119, 6, 0.15)",
        backgroundDark: "rgba(251, 191, 36, 0.2)",
      });
    }

    if (!user.approved) {
      pills.push({
        label: "Approval pending",
        color: "#92400e",
        darkColor: "#fbbf24",
        backgroundLight: "rgba(217, 119, 6, 0.15)",
        backgroundDark: "rgba(251, 191, 36, 0.2)",
      });
    }

    const passwordLoginApplies =
      !enterprise?.require_sso_for_sign_in && !user.password_login_disabled;
    const userHasPassword = Boolean(user.obscuredPassword);

    if (!enterprise?.require_sso_for_sign_in && user.password_login_disabled) {
      pills.push({
        label: "Password login disabled",
        color: accentColor,
        darkColor: getReadableAccentColor(accentColor, true),
        backgroundLight: accentBackgroundLight,
        backgroundDark: accentBackgroundDark,
      });
    } else if (passwordLoginApplies && !userHasPassword) {
      pills.push({
        label: "Invite pending",
        color: "#92400e",
        darkColor: "#fbbf24",
        backgroundLight: "rgba(217, 119, 6, 0.15)",
        backgroundDark: "rgba(251, 191, 36, 0.2)",
      });
    }

    return pills;
  };

  getLoginSummary = (user) => {
    const enterprise = this.props.resources?.userEnterprise;
    if (enterprise?.require_sso_for_sign_in) {
      return "SSO required";
    }
    if (user.password_login_disabled) {
      return "SSO only";
    }
    if (!user.obscuredPassword) {
      return "Password not set";
    }
    return "Password login enabled";
  };

  openAddUserModal = () => {
    this.setState({
      addUserModalOpen: true,
      addUserMode: "invite",
      modalSubmitting: false,
      modalError: "",
      inviteEmail: "",
      inviteHasAdminPrivileges: false,
      invitePasswordLoginDisabled: false,
      inviteSendSetupEmail: true,
      invitePDUs: [],
    });
  };

  closeAddUserModal = () => {
    if (this.state.modalSubmitting) {
      return;
    }
    this.setState({
      addUserModalOpen: false,
      modalSubmitting: false,
      modalError: "",
      inviteEmail: "",
      inviteHasAdminPrivileges: false,
      invitePasswordLoginDisabled: false,
      inviteSendSetupEmail: true,
      invitePDUs: [],
    });
  };

  handleInviteSubmit = () => {
    const {
      inviteEmail,
      inviteHasAdminPrivileges,
      invitePasswordLoginDisabled,
      inviteSendSetupEmail,
      invitePDUs,
      modalSubmitting,
    } = this.state;
    if (modalSubmitting) {
      return;
    }

    if (!inviteEmail.trim()) {
      this.setState({ modalError: "Please enter an email address." });
      return;
    }

    const foundEnterprise = this.props.resources.userEnterprise;
    const sendInviteLink = Boolean(
      inviteSendSetupEmail &&
        !foundEnterprise?.require_sso_for_sign_in &&
        !invitePasswordLoginDisabled
    );
    this.setState({ modalSubmitting: true, modalError: "" });

    axios
      .post(
        `/v1/enterprises/${foundEnterprise?.id}/users`,
        {
          email_id: inviteEmail.trim(),
          has_admin_privileges: inviteHasAdminPrivileges,
          password_login_disabled: invitePasswordLoginDisabled,
          pdu_access_list: invitePDUs,
          send_invite: sendInviteLink,
        },
        {
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        }
      )
      .then((res) => {
        const inviteSent = Boolean(res?.data?.invite_sent);
        NotificationManager.success(
          inviteSent
            ? "User added and setup email sent"
            : "User added successfully"
        );
        const createdUser = res?.data?.data;
        this.props.updateFetchWrapperState({
          userEnterprise: {
            ...this.props.resources.userEnterprise,
            users: [
              ...this.props.resources.userEnterprise.users,
              createdUser,
            ],
          },
        });
        this.setState({
          addUserModalOpen: false,
          modalSubmitting: false,
          inviteEmail: "",
          inviteHasAdminPrivileges: false,
          invitePasswordLoginDisabled: false,
          inviteSendSetupEmail: true,
          invitePDUs: [],
        });
        if (createdUser?.user_id) {
          this.props.history.push(`/team/members/${createdUser.user_id}`);
        }
      })
      .catch((err) => {
        console.log(err);
        this.setState({
          modalSubmitting: false,
          modalError:
            err?.response?.data?.message ||
            "Unable to create the user. Please try again.",
        });
      });
  };

  handleUsersPageChange = (pageUsers, pager) => {
    this.setState({ usersPager: pager, usersPageItems: pageUsers });
  };

  renderTeamMemberRow = (user, index) => {
    const statusPills = this.getEnterpriseStatusPills(user);
    const loginSummary = this.getLoginSummary(user);
    const isAdmin = user.is_enterprise_admin || user.has_admin_privileges;
    const email = user.email_id;
    const roleName =
      user.role?.name || getRoleName(this.props.resources.roles || [], user.role_id);
    const { dark, primaryColor, primaryFontColor } = this.state;
    const accentColor = primaryColor || DEFAULT_PRIMARY_COLOR;
    const accentReadableColor = getReadableAccentColor(accentColor, dark);
    const accentTintLight = applyAlphaToColor(
      accentColor,
      0.12,
      "rgba(29, 78, 216, 0.12)"
    );
    const accentTintDark = applyAlphaToColor(
      accentColor,
      0.28,
      "rgba(59, 130, 246, 0.28)"
    );
    const rowBorderColor = dark ? "rgba(148, 163, 184, 0.2)" : "#e5e7eb";
    const rowBackground = dark ? "rgba(30, 41, 59, 0.75)" : "#fff";
    const emailColor = dark ? "#e2e8f0" : "#4b5563";
    const idColor = dark ? "#94a3b8" : "#6b7280";
    const loginColor = dark ? "#d1d5db" : "#374151";
    const adminTextColor = dark ? "#4ade80" : "#065f46";
    const adminBackground = dark
      ? "rgba(34, 197, 94, 0.24)"
      : "rgba(16, 185, 129, 0.14)";
    const memberTextColor = dark
      ? accentReadableColor || primaryFontColor || "#bfdbfe"
      : accentColor;
    const memberBackground = dark ? accentTintDark : accentTintLight;
    const secondaryTextColor = dark ? "#cbd5e1" : "#4b5563";
    const subtleTextColor = dark ? "#64748b" : "#9ca3af";
    const actionLinkColor = dark
      ? accentReadableColor || "#90caf9"
      : accentColor;

    return (
      <div
        key={user.paginationId || index}
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(220px, 2.6fr) minmax(180px, 1.6fr) minmax(150px, 1fr) minmax(120px, 0.9fr) minmax(240px, 2fr) minmax(120px, 0.9fr)",
          gap: "16px",
          padding: "16px",
          borderTop: index === 0 ? "none" : `1px solid ${rowBorderColor}`,
          alignItems: "flex-start",
          background: rowBackground,
        }}
      >
        <div>
          {/* <div
            style={{
              fontWeight: 600,
              fontSize: "15px",
              color: "#111827",
              wordBreak: "break-word",
            }}
          >
            {name}
          </div> */}
          {email && (
            <div
              style={{
                color: emailColor,
                marginTop: "4px",
                fontWeight: 600,
                fontSize: "15px",
              }}
            >
              <Link
                to={`/team/members/${user.user_id}`}
                style={{
                  textDecoration: "underline",
                  color: "inherit",
                  wordBreak: "break-all",
                }}
              >
                {email}
              </Link>
            </div>
          )}
          <div
            style={{
              color: idColor,
              marginTop: "6px",
              fontSize: "12px",
            }}
          >
            User ID: {user.user_id || user.id || "N/A"}
          </div>
        </div>

        <div>
          {statusPills.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
              }}
            >
              {statusPills.map((pill, pillIndex) => (
                <span
                  key={`${user.paginationId}-pill-${pillIndex}`}
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    padding: "4px 9px",
                    borderRadius: "999px",
                    color: dark && pill.darkColor ? pill.darkColor : pill.color,
                    background: dark
                      ? pill.backgroundDark || pill.background || applyAlphaToColor(pill.color, 0.2, "rgba(148, 163, 184, 0.2)")
                      : pill.backgroundLight || pill.background || applyAlphaToColor(pill.color, 0.12, "rgba(148, 163, 184, 0.12)"),
                  }}
                >
                  {pill.label}
                </span>
              ))}
            </div>
          ) : (
            <div style={{ color: subtleTextColor, fontSize: "12px" }}>—</div>
          )}
        </div>

        <div style={{ color: loginColor, fontSize: "13px" }}>{loginSummary}</div>

        <div>
          <span
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: isAdmin ? adminTextColor : memberTextColor,
              background: isAdmin ? adminBackground : memberBackground,
              padding: "4px 10px",
              borderRadius: "999px",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              whiteSpace: "nowrap",
            }}
          >
            {isAdmin ? "Admin" : "Member"}
          </span>
          {roleName ? (
            <div
              style={{
                color: secondaryTextColor,
                fontSize: "12px",
                marginTop: "8px",
                wordBreak: "break-word",
              }}
            >
              {roleName}
            </div>
          ) : null}
        </div>

        <div>
          {isAdmin ? (
            <div style={{ color: secondaryTextColor, fontSize: "13px" }}>
              All PDUs allowed
            </div>
          ) : this.props.resources.devicesLoading ? (
            <Loading />
          ) : (
            <PDUAccessDropdown
              pdu_access_list={user.pdu_access_list}
              editable={false}
              devices={this.props.resources.devices || []}
              devicesLoading={this.props.resources.devicesLoading}
              groups={this.props.resources.groups || []}
            />
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Link
            to={`/team/members/${user.user_id}`}
            style={{
              fontSize: "12px",
              color: actionLinkColor,
              textDecoration: "underline",
              fontWeight: 600,
            }}
          >
            View profile
          </Link>
        </div>
      </div>
    );
  };

  renderAddUserModal = () => {
    if (!this.state.addUserModalOpen) {
      return null;
    }

    const {
      addUserMode,
      modalSubmitting,
      modalError,
      inviteEmail,
      inviteHasAdminPrivileges,
      invitePasswordLoginDisabled,
      inviteSendSetupEmail,
      invitePDUs,
    } = this.state;
    const { dark, primaryColor, primaryFontColor } = this.state;

    const modalOptions = [{ key: "invite", label: "Invite new user" }];
    const sharedActionButtonStyle = {
      border: "none",
      borderRadius: "6px",
      padding: "10px 18px",
      fontWeight: 600,
      fontSize: "14px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      transition: "background-color 0.2s ease, color 0.2s ease, opacity 0.2s ease",
    };
    const accentColor = primaryColor || DEFAULT_PRIMARY_COLOR;
    const accentFontColor = primaryFontColor || DEFAULT_PRIMARY_FONT_COLOR;
    const accentReadableColor = getReadableAccentColor(accentColor, dark);
    const surfaceColor = dark ? "#1f2937" : "#fff";
    const dividerColor = dark ? "rgba(148, 163, 184, 0.2)" : "#e5e7eb";
    const secondaryTextColor = dark ? "#cbd5e1" : "#6b7280";
    const headingColor = dark ? "#f8fafc" : "#111827";
    const enterpriseRequiresSso = Boolean(
      this.props.resources?.userEnterprise?.require_sso_for_sign_in,
    );
    const inviteLinkDisabled = enterpriseRequiresSso || invitePasswordLoginDisabled;
    const sendSetupEmailValue = inviteLinkDisabled ? false : inviteSendSetupEmail;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: dark ? "rgba(15, 23, 42, 0.7)" : "rgba(15, 23, 42, 0.45)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
          padding: "24px",
        }}
        onClick={this.closeAddUserModal}
      >
        <div
          style={{
            background: surfaceColor,
            borderRadius: "12px",
            width: "min(760px, 100%)",
            maxHeight: "calc(100vh - 48px)",
            overflow: "hidden",
            boxShadow: dark
              ? "0 20px 50px rgba(15, 23, 42, 0.65)"
              : "0 20px 50px rgba(15, 23, 42, 0.35)",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              padding: "20px 24px",
              borderBottom: `1px solid ${dividerColor}`,
              gap: "16px",
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "18px",
                  color: headingColor,
                }}
              >
                Invite a new teammate
              </div>
              <div
                style={{
                  color: secondaryTextColor,
                  marginTop: "4px",
                }}
              >
                Create an account, optionally email a setup link, and configure
                their initial access without leaving this page.
              </div>
            </div>
            <button
              onClick={this.closeAddUserModal}
              style={{
                background: "transparent",
                border: "none",
                color: secondaryTextColor,
                fontSize: "20px",
                cursor: "pointer",
                lineHeight: 1,
              }}
              aria-label="Close"
              type="button"
              disabled={modalSubmitting}
            >
              ×
            </button>
          </div>
          <div
            style={{
              padding: "0 24px 24px",
              overflowY: "auto",
              flex: 1,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                border: `1px solid ${dark ? "rgba(148, 163, 184, 0.3)" : "#d1d5db"}`,
                borderRadius: "999px",
                padding: "4px",
                background: dark ? "rgba(148, 163, 184, 0.12)" : "#f9fafb",
                marginBottom: "20px",
                marginTop: "24px",
                gap: "4px",
              }}
            >
              {modalOptions.map((option) => {
                const isActive = addUserMode === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      if (!modalSubmitting) {
                        this.setState({ addUserMode: option.key, modalError: "" });
                      }
                    }}
                    style={{
                      border: "none",
                      borderRadius: "999px",
                      padding: "8px 18px",
                    fontWeight: 600,
                    fontSize: "13px",
                    cursor: modalOptions.length === 1 ? "default" : "pointer",
                    color: isActive
                      ? accentFontColor
                      : dark
                        ? "#cbd5e1"
                        : "#374151",
                    background: isActive ? accentColor : "transparent",
                  }}
                  disabled={modalOptions.length === 1}
                >
                  {option.label}
                </button>
                );
              })}
            </div>

            <div style={{ maxWidth: "520px" }}>
              <TextInput
                label="Email"
                placeholder="example@domain.com"
                value={inviteEmail}
                callback={(value) => this.setState({ inviteEmail: value })}
                inputContainerStyle={{ marginBottom: "16px" }}
                disabled={modalSubmitting}
              />

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <ToggleInput
                  label="Admin privileges"
                  description="Admins can manage teams, groups, and enterprise settings."
                  value={inviteHasAdminPrivileges}
                  type="checkbox"
                  callback={(value) =>
                    this.setState({ inviteHasAdminPrivileges: value })
                  }
                  id="invite-admin-toggle"
                  disabled={modalSubmitting}
                />
                <ToggleInput
                  label="Disable password login"
                  description={
                    enterpriseRequiresSso
                      ? "Password sign-in is disabled by enterprise SSO policy."
                      : "Require this user to sign in with SSO only."
                  }
                  value={enterpriseRequiresSso || invitePasswordLoginDisabled}
                  type="checkbox"
                  callback={(value) =>
                    this.setState({ invitePasswordLoginDisabled: value })
                  }
                  id="invite-password-toggle"
                  disabled={modalSubmitting || enterpriseRequiresSso}
                />
                <ToggleInput
                  label="Send setup link"
                  description={
                    inviteLinkDisabled
                      ? "Setup links are only sent for password-based accounts."
                      : "Email a one-time setup link so the user can set their password."
                  }
                  value={sendSetupEmailValue}
                  type="checkbox"
                  callback={(value) =>
                    this.setState({ inviteSendSetupEmail: value })
                  }
                  id="invite-setup-link-toggle"
                  disabled={modalSubmitting || inviteLinkDisabled}
                />
              </div>

              <div
                style={{
                  fontWeight: 600,
                  fontSize: "13px",
                  color: dark ? "#e2e8f0" : "#374151",
                }}
              >
                Assign device access
              </div>
              <div style={{ marginTop: "8px" }}>
                {this.props.resources.devicesLoading ? (
                  <Loading />
                ) : (
                  <PDUAccessDropdown
                    pdu_access_list={invitePDUs}
                    devices={this.props.resources.devices || []}
                    devicesLoading={this.props.resources.devicesLoading}
                    groups={this.props.resources.groups || []}
                    onPduAccessListChange={(updated) =>
                      this.setState({ invitePDUs: updated })
                    }
                  />
                )}
              </div>
            </div>

            {modalError && (
              <div
                style={{
                  color: dark ? "#fecaca" : "#b71c1c",
                  background: dark
                    ? "rgba(248, 113, 113, 0.18)"
                    : "rgba(183, 28, 28, 0.08)",
                  padding: "10px 14px",
                  borderRadius: "6px",
                  marginTop: "20px",
                }}
              >
                {modalError}
              </div>
            )}
          </div>
          <div
            style={{
              padding: "16px 24px",
              borderTop: `1px solid ${dividerColor}`,
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
            }}
          >
            <button
              type="button"
              onClick={this.closeAddUserModal}
              style={{
                ...sharedActionButtonStyle,
                background: dark ? "#374151" : "#e5e7eb",
                color: dark ? "#f1f5f9" : "#111827",
                border: dark ? "1px solid rgba(148, 163, 184, 0.3)" : "none",
                cursor: modalSubmitting ? "not-allowed" : "pointer",
              }}
              disabled={modalSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={this.handleInviteSubmit}
              style={{
                ...sharedActionButtonStyle,
                background: accentColor,
                color: dark
                  ? accentReadableColor || accentFontColor
                  : accentFontColor,
                minWidth: "140px",
                cursor: modalSubmitting ? "not-allowed" : "pointer",
                opacity: modalSubmitting ? 0.7 : 1,
              }}
              disabled={modalSubmitting}
            >
              {modalSubmitting ? <Loading /> : "Invite user"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const filteredUsers = this.getFilteredUsers();
    const { usersPager, usersPageItems, dark } = this.state;
    const roleOptions = this.getRoleOptions();
    const hasActiveFilters = Boolean(
      this.state.searchTerm.trim() ||
      this.state.statusFilter !== "all" ||
      this.state.adminFilter !== "all" ||
      this.state.roleFilter !== "all"
    );
    const totalUsers = filteredUsers.length;
    const pageSize = usersPager?.pageSize || 10;
    const showingStart = totalUsers === 0
      ? 0
      : usersPager
      ? usersPager.startIndex + 1
      : 1;
    const showingEnd = totalUsers === 0
      ? 0
      : usersPager
      ? Math.min(usersPager.endIndex + 1, totalUsers)
      : Math.min(pageSize, totalUsers);
    const currentPageUsers = usersPageItems.length
      ? usersPageItems
      : filteredUsers.slice(0, Math.min(pageSize, filteredUsers.length));
    const columnTemplate =
      "minmax(220px, 2.6fr) minmax(180px, 1.6fr) minmax(150px, 1fr) minmax(120px, 0.9fr) minmax(240px, 2fr) minmax(120px, 0.9fr)";
    const panelBorderColor = dark ? "rgba(148, 163, 184, 0.2)" : "#e5e7eb";
    const panelBackground = dark ? "#111827" : "#fff";
    const panelHeaderBackground = dark ? "rgba(148, 163, 184, 0.12)" : "#f9fafb";
    const panelHeaderTextColor = dark ? "#cbd5e1" : "#4b5563";
    const emptyStateBorder = dark ? "rgba(148, 163, 184, 0.4)" : "#d1d5db";
    const emptyStateText = dark ? "#cbd5e1" : "#6b7280";
    const toolbarInputStyle = { margin: 0 };

    return (
      <div className="cell large-12">
        {this.renderAddUserModal()}
        <div
          style={{ marginTop: "10px", marginBottom: "20px" }}
        >
          <AppPageHeader
            title="Team members"
            description="Invite teammates and review who has access to your enterprise."
            actions={
              <button
                type="button"
                className="button button--icon-label app-page__header-action-button"
                onClick={this.openAddUserModal}
              >
                <i className="material-icons" aria-hidden="true">person_add</i>
                <span>Invite user</span>
              </button>
            }
          />
        </div>

        <AppToolbar variant="plain">
          <AppToolbarGrid wide withActions>
            <AppToolbarField label="Search members" grow>
              <div className="app-toolbar__search">
                <input
                  type="text"
                  placeholder="Search by name or email"
                  value={this.state.searchTerm}
                  onChange={(event) =>
                    this.updateMemberFilter("searchTerm", event.target.value)
                  }
                  style={toolbarInputStyle}
                />
                <button
                  type="button"
                  className="search-icon-button"
                  aria-label={
                    this.state.searchTerm ? "Clear member search" : "Member search"
                  }
                  onClick={() => {
                    if (this.state.searchTerm) {
                      this.updateMemberFilter("searchTerm", "");
                    }
                  }}
                  disabled={!this.state.searchTerm}
                  style={{
                    cursor: this.state.searchTerm ? "pointer" : "default",
                    opacity: this.state.searchTerm ? 1 : 0.72,
                  }}
                >
                  <i className="material-icons">
                    {this.state.searchTerm ? "close" : "search"}
                  </i>
                </button>
              </div>
            </AppToolbarField>
            <AppToolbarField label="Status">
              <select
                value={this.state.statusFilter}
                onChange={(event) =>
                  this.updateMemberFilter("statusFilter", event.target.value)
                }
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </AppToolbarField>
            <AppToolbarField label="Admin access">
              <select
                value={this.state.adminFilter}
                onChange={(event) =>
                  this.updateMemberFilter("adminFilter", event.target.value)
                }
              >
                <option value="all">Admins and members</option>
                <option value="admin">Admins only</option>
                <option value="member">Members only</option>
              </select>
            </AppToolbarField>
            <AppToolbarField label="Permission role">
              <select
                value={this.state.roleFilter}
                onChange={(event) =>
                  this.updateMemberFilter("roleFilter", event.target.value)
                }
              >
                <option value="all">All roles</option>
                <option value="none">No assigned role</option>
                {roleOptions.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </AppToolbarField>
          </AppToolbarGrid>
          <div className="app-toolbar__meta">
            <span>
              {totalUsers === 0
                ? "Showing 0 of 0 members"
                : `Showing ${showingStart}-${showingEnd} of ${totalUsers} members`}
            </span>
            {hasActiveFilters ? (
              <button
                type="button"
                className="button secondary app-toolbar__action-button"
                onClick={this.clearMemberFilters}
              >
                Clear filters
              </button>
            ) : null}
          </div>
        </AppToolbar>

        <div style={{ marginTop: "24px" }}>
          {filteredUsers.length ? (
            <div style={{ overflowX: "auto" }}>
              <div
                style={{
                  minWidth: "920px",
                  border: `1px solid ${panelBorderColor}`,
                  borderRadius: "3px",
                  overflow: "hidden",
                  background: panelBackground,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: columnTemplate,
                    gap: "16px",
                    padding: "12px 16px",
                    background: panelHeaderBackground,
                    color: panelHeaderTextColor,
                    fontSize: "11px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  <div>Member</div>
                  <div>Status</div>
                  <div>Login</div>
                  <div>Role</div>
                  <div>Device access</div>
                  <div style={{ textAlign: "right" }}>Actions</div>
                </div>
                {currentPageUsers.map((user, index) =>
                  this.renderTeamMemberRow(user, index)
                )}
              </div>
              <div style={{ marginTop: "16px" }}>
                <Pagination
                  items={filteredUsers}
                  renderItem={() => null}
                  onPageChange={this.handleUsersPageChange}
                />
              </div>
            </div>
          ) : (
            <div
              style={{
                border: `1px dashed ${emptyStateBorder}`,
                padding: "32px",
                borderRadius: "10px",
                textAlign: "center",
                color: emptyStateText,
              }}
            >
              {hasActiveFilters
                ? "No members match the current search or filters."
                : "No team members found. Invite your first teammate to get started."}
            </div>
          )}
        </div>
      </div>
    );
  }
}
